import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ShieldAlert, Store } from 'lucide-react';
import Button from '../../components/Button';
import { useAuth } from '../../context/AuthContext';
import { getStoreHours, setEmergencyStatus } from '../../api/store';
import styles from './EmergencyCloseCard.module.css';

export default function EmergencyCloseCard() {
  const { user } = useAuth();
  const [emergencyClosed, setEmergencyClosed] = useState(false);
  const [closedReason, setClosedReason] = useState(null);
  const [reasonDraft, setReasonDraft] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const store = await getStoreHours(user.idToken);
      setEmergencyClosed(store.emergencyClosed);
      setClosedReason(store.emergencyReason);
    } catch (err) {
      setLoadError(err.message || 'Could not load store status.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleClose = async () => {
    setSubmitting(true);
    setActionError('');
    try {
      const store = await setEmergencyStatus({ closed: true, reason: reasonDraft.trim() || undefined }, user.idToken);
      setEmergencyClosed(store.emergencyClosed);
      setClosedReason(store.emergencyReason);
      setReasonDraft('');
      setConfirming(false);
    } catch (err) {
      setActionError(err.message || 'Could not close the store.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    setSubmitting(true);
    setActionError('');
    try {
      const store = await setEmergencyStatus({ closed: false }, user.idToken);
      setEmergencyClosed(store.emergencyClosed);
      setClosedReason(store.emergencyReason);
    } catch (err) {
      setActionError(err.message || 'Could not reopen the store.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.iconWrap}><ShieldAlert size={18} /></span>
        <div className={styles.headText}>
          <p className={styles.title}>Emergency close</p>
          <p className={styles.subtitle}>Pause new orders immediately without changing your weekly hours.</p>
        </div>
        {!loading && !loadError && (
          <span className={`${styles.statusPill} ${emergencyClosed ? styles.closed : styles.open}`}>
            <span className={styles.statusDot} />
            {emergencyClosed ? 'Closed — Emergency' : 'Open'}
          </span>
        )}
      </div>

      <div className={styles.body}>
        {loading && <p className={styles.hint}>Loading store status…</p>}

        {!loading && loadError && (
          <div className={styles.hint}>
            <p className={styles.warning}><AlertTriangle size={12} /> {loadError}</p>
            <Button type="button" variant="outline" size="sm" onClick={loadStatus} style={{ marginTop: 8 }}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !loadError && (
          emergencyClosed ? (
            <>
              <p className={styles.statusNote}>
                <AlertTriangle size={14} />
                Store is closed to new orders{closedReason ? `: "${closedReason}"` : '.'}
              </p>
              {actionError && <p className={styles.warning}><AlertTriangle size={12} /> {actionError}</p>}
              <Button variant="secondary" size="sm" icon={Store} onClick={handleReopen} disabled={submitting} className={styles.actionBtn}>
                {submitting ? 'Reopening…' : 'Reopen store'}
              </Button>
            </>
          ) : confirming ? (
            <>
              <p className={styles.hint}>
                New orders pause right away — customers will see the store as closed until you reopen it.
                Orders already in progress are not affected.
              </p>
              <input
                type="text"
                className={styles.reasonInput}
                placeholder="Reason (optional) — e.g. Gas leak nearby"
                value={reasonDraft}
                onChange={(e) => setReasonDraft(e.target.value)}
                disabled={submitting}
              />
              {actionError && <p className={styles.warning}><AlertTriangle size={12} /> {actionError}</p>}
              <div className={styles.actions}>
                <Button variant="ghost" size="sm" onClick={() => { setConfirming(false); setActionError(''); }} disabled={submitting}>
                  Cancel
                </Button>
                <Button variant="danger" size="sm" onClick={handleClose} disabled={submitting}>
                  {submitting ? 'Closing…' : 'Close store now'}
                </Button>
              </div>
            </>
          ) : (
            <Button variant="danger" size="sm" icon={ShieldAlert} onClick={() => setConfirming(true)} className={styles.actionBtn}>
              Close store now
            </Button>
          )
        )}
      </div>
    </div>
  );
}
